import { create } from 'zustand';
import { api, apiErrorMessage, type Paginated } from '@/lib/api';
import { asArray, asNumber } from '@/lib/collections';
import { toast } from './ui.store';
import type { PublicPost } from '@ai-media/shared';

export interface PostFilters {
  status?: string;
  platform?: string;
  campaignId?: string;
  search?: string;
  page: number;
}

interface ContentState {
  posts: PublicPost[];
  total: number;
  loading: boolean;
  filters: PostFilters;
  fetchPosts: (filters?: Partial<PostFilters>) => Promise<void>;
  setFilters: (patch: Partial<PostFilters>) => void;
  approve: (id: string) => Promise<void>;
  publishNow: (id: string) => Promise<void>;
  cancel: (id: string) => Promise<void>;
  remove: (id: string) => Promise<void>;
}

const defaultFilters: PostFilters = { page: 1 };

export const useContentStore = create<ContentState>((set, get) => ({
  posts: [],
  total: 0,
  loading: false,
  filters: defaultFilters,

  setFilters: (patch) => set((state) => ({ filters: { ...state.filters, ...patch, page: 1 } })),

  fetchPosts: async (patch) => {
    const filters = { ...get().filters, ...patch };
    set({ filters, loading: true });
    try {
      const { data } = await api.get<Paginated<PublicPost>>('/content/posts', {
        params: {
          status: filters.status,
          platform: filters.platform,
          campaignId: filters.campaignId,
          search: filters.search,
          page: filters.page,
          limit: 20,
        },
      });
      set({ posts: asArray(data?.data), total: asNumber(data?.total) });
    } catch (error) {
      toast(apiErrorMessage(error), 'danger');
    } finally {
      set({ loading: false });
    }
  },

  approve: async (id) => {
    await api.post(`/content/posts/${id}/approve`);
    toast('Post approved', 'success');
    await get().fetchPosts();
  },

  publishNow: async (id) => {
    try {
      await api.post(`/content/posts/${id}/publish`);
      toast('Publishing started', 'success');
      await get().fetchPosts();
    } catch (error) {
      toast(apiErrorMessage(error), 'danger');
    }
  },

  cancel: async (id) => {
    await api.post(`/content/posts/${id}/cancel`);
    toast('Post cancelled', 'medium');
    await get().fetchPosts();
  },

  remove: async (id) => {
    await api.delete(`/content/posts/${id}`);
    toast('Post deleted', 'medium');
    await get().fetchPosts();
  },
}));
